import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface ServerPaginationProps {
  currentPage: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  pageSizeOptions?: number[];
  itemLabel?: string;
  themeColor?: 'purple' | 'amber' | 'emerald' | 'blue';
}

export const ServerPagination: React.FC<ServerPaginationProps> = ({
  currentPage,
  pageSize,
  totalRecords,
  totalPages,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  itemLabel = 'bản ghi',
  themeColor = 'purple'
}) => {
  if (totalRecords === 0) return null;

  const startRecord = (currentPage - 1) * pageSize + 1;
  const endRecord = Math.min(currentPage * pageSize, totalRecords);

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        start = 2;
        end = 4;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
        end = totalPages - 1;
      }

      if (start > 2) {
        pages.push('...');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  const themeStyles = {
    purple: {
      activeBg: 'bg-purple-600 text-white shadow-xs',
      activeBadge: 'bg-purple-50 text-purple-700 border-purple-200',
      focusRing: 'focus:ring-purple-500/20 focus:border-purple-500'
    },
    amber: {
      activeBg: 'bg-amber-600 text-white shadow-xs',
      activeBadge: 'bg-amber-50 text-amber-800 border-amber-200',
      focusRing: 'focus:ring-amber-500/20 focus:border-amber-500'
    },
    emerald: {
      activeBg: 'bg-emerald-600 text-white shadow-xs',
      activeBadge: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      focusRing: 'focus:ring-emerald-500/20 focus:border-emerald-500'
    },
    blue: {
      activeBg: 'bg-blue-600 text-white shadow-xs',
      activeBadge: 'bg-blue-50 text-blue-800 border-blue-200',
      focusRing: 'focus:ring-blue-500/20 focus:border-blue-500'
    }
  }[themeColor];

  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-600">
      {/* Records Info & Page Size Selector */}
      <div className="flex flex-wrap items-center gap-3">
        <div>
          Hiển thị từ <span className="font-bold text-slate-900">{startRecord.toLocaleString('vi-VN')}</span> đến{' '}
          <span className="font-bold text-slate-900">{endRecord.toLocaleString('vi-VN')}</span> trên tổng số{' '}
          <span className="font-bold text-slate-900">{totalRecords.toLocaleString('vi-VN')}</span> {itemLabel}
        </div>

        <div className="flex items-center space-x-1.5 pl-2 border-l border-slate-200">
          <span className="text-slate-500 text-[11px]">Hiển thị:</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className={`bg-white border border-slate-200 rounded-lg py-1 px-2 text-xs font-semibold text-slate-700 outline-none cursor-pointer hover:border-slate-300 transition-colors ${themeStyles.focusRing}`}
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt} / trang
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center space-x-1">
        {/* First Page */}
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(1)}
          className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white text-slate-600 transition-colors cursor-pointer disabled:cursor-not-allowed"
          title="Trang đầu"
        >
          <ChevronsLeft className="w-4 h-4" />
        </button>

        {/* Previous Page */}
        <button
          type="button"
          disabled={currentPage <= 1}
          onClick={() => onPageChange(currentPage - 1)}
          className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white text-slate-600 transition-colors cursor-pointer disabled:cursor-not-allowed"
          title="Trang trước"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        {/* Page Numbers */}
        <div className="flex items-center space-x-1 px-1">
          {getPageNumbers().map((p, idx) => {
            if (p === '...') {
              return (
                <span key={`ellipsis-${idx}`} className="px-2 py-1 text-slate-400 font-bold">
                  ...
                </span>
              );
            }

            const pageNum = p as number;
            const isActive = pageNum === currentPage;

            return (
              <button
                key={pageNum}
                type="button"
                onClick={() => onPageChange(pageNum)}
                className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                  isActive
                    ? themeStyles.activeBg
                    : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        {/* Next Page */}
        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(currentPage + 1)}
          className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white text-slate-600 transition-colors cursor-pointer disabled:cursor-not-allowed"
          title="Trang sau"
        >
          <ChevronRight className="w-4 h-4" />
        </button>

        {/* Last Page */}
        <button
          type="button"
          disabled={currentPage >= totalPages}
          onClick={() => onPageChange(totalPages)}
          className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-30 disabled:hover:bg-white text-slate-600 transition-colors cursor-pointer disabled:cursor-not-allowed"
          title="Trang cuối"
        >
          <ChevronsRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
